import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  eachWeekOfInterval,
  addDays,
  addYears,
  differenceInDays,
  isWithinInterval,
  parseISO,
  getISOWeek,
  getYear,
  isWeekend,
  startOfYear
} from 'date-fns';

// Italian month abbreviations
const monthAbbreviations = {
  0: 'Gen', 1: 'Feb', 2: 'Mar', 3: 'Apr', 4: 'Mag', 5: 'Giu',
  6: 'Lug', 7: 'Ago', 8: 'Set', 9: 'Ott', 10: 'Nov', 11: 'Dic'
};

// Format month for header (e.g., "Gen26")
export const formatMonthYear = (date) => {
  const month = monthAbbreviations[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return month + year;
};

// Format date for day view (e.g., "15")
export const formatDayNumber = (date) => {
  return format(date, 'd');
};

// Format date for week view (e.g., "01/01/26-07/01/26")
export const formatWeekRange = (weekStart) => {
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  return format(weekStart, 'dd/MM/yy') + '-' + format(weekEnd, 'dd/MM/yy');
};

// Get ISO week string (e.g., "2026-W02")
export const getWeekKey = (date) => {
  const year = getYear(date);
  const week = getISOWeek(date);
  return year + '-W' + week.toString().padStart(2, '0');
};

// Generate day columns for a date range
export const generateDayColumns = (startDate, endDate) => {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const days = eachDayOfInterval({ start: start, end: end });
  
  const months = [];
  let currentMonth = null;
  
  for (let i = 0; i < days.length; i++) {
    const day = days[i];
    const monthKey = formatMonthYear(day);
    if (!currentMonth || currentMonth.key !== monthKey) {
      currentMonth = { 
        key: monthKey, 
        days: [],
        startIndex: months.reduce(function(acc, m) { return acc + m.days.length; }, 0)
      };
      months.push(currentMonth);
    }
    currentMonth.days.push({
      date: day,
      label: formatDayNumber(day),
      isWeekend: isWeekend(day),
      isFirstOfMonth: day.getDate() === 1
    });
  }
  
  return { months: months, totalDays: days.length };
};

// Generate week columns for a date range
export const generateWeekColumns = (startDate, endDate) => {
  const start = startOfWeek(parseISO(startDate), { weekStartsOn: 1 });
  const end = endOfWeek(parseISO(endDate), { weekStartsOn: 1 });
  const weeks = eachWeekOfInterval({ start: start, end: end }, { weekStartsOn: 1 });
  
  const months = [];
  let currentMonth = null;
  
  for (let i = 0; i < weeks.length; i++) {
    const weekStart = weeks[i];
    const monthKey = formatMonthYear(weekStart);
    if (!currentMonth || currentMonth.key !== monthKey) {
      currentMonth = { 
        key: monthKey, 
        weeks: [],
        startIndex: months.reduce(function(acc, m) { return acc + m.weeks.length; }, 0)
      };
      months.push(currentMonth);
    }
    currentMonth.weeks.push({
      date: weekStart,
      label: formatWeekRange(weekStart),
      weekKey: getWeekKey(weekStart)
    });
  }
  
  return { months: months, totalWeeks: weeks.length };
};

// Calculate task position and width for day view
export const calculateTaskPositionDays = (taskStart, taskEnd, gridStart) => {
  const start = parseISO(taskStart);
  const end = parseISO(taskEnd);
  const gridStartDate = parseISO(gridStart);
  
  const startOffset = Math.max(0, differenceInDays(start, gridStartDate));
  const duration = differenceInDays(end, start) + 1;
  
  return { startOffset: startOffset, duration: duration };
};

// Calculate task position and width for week view
export const calculateTaskPositionWeeks = (taskStart, taskEnd, gridStart) => {
  const start = parseISO(taskStart);
  const end = parseISO(taskEnd);
  const gridStartDate = startOfWeek(parseISO(gridStart), { weekStartsOn: 1 });
  
  const startOffset = Math.floor(differenceInDays(start, gridStartDate) / 7);
  const endWeekOffset = Math.floor(differenceInDays(end, gridStartDate) / 7);
  const duration = endWeekOffset - startOffset + 1;
  
  return { startOffset: Math.max(0, startOffset), duration: duration };
};

// Count working days in a week (Mon-Fri)
export const countWorkingDaysInWeek = (weekStart) => {
  const days = eachDayOfInterval({
    start: weekStart,
    end: addDays(weekStart, 6)
  });
  let count = 0;
  for (let i = 0; i < days.length; i++) {
    if (!isWeekend(days[i])) count++;
  }
  return count;
};

// Convert hours to working days (assuming 8h per day)
export const hoursToWorkingDays = (hours) => {
  return hours / 8;
};

// Check if a date is within a task's date range
export const isDateInTaskRange = (date, taskStart, taskEnd) => {
  const d = typeof date === 'string' ? parseISO(date) : date;
  const start = parseISO(taskStart);
  const end = parseISO(taskEnd);
  return isWithinInterval(d, { start: start, end: end });
};

// Get all weeks between two dates
export const getWeeksBetween = (startDate, endDate) => {
  const start = startOfWeek(parseISO(startDate), { weekStartsOn: 1 });
  const end = endOfWeek(parseISO(endDate), { weekStartsOn: 1 });
  const weeks = eachWeekOfInterval({ start: start, end: end }, { weekStartsOn: 1 });
  const result = [];
  for (let i = 0; i < weeks.length; i++) {
    result.push(getWeekKey(weeks[i]));
  }
  return result;
};

// Get week date range from week key (e.g., "2026-W02" -> {start: "29/12", end: "04/01"})
export const getWeekDateRange = (weekKey) => {
  // Parse weekKey like "2026-W02"
  const parts = weekKey.split('-W');
  const year = parseInt(parts[0]);
  const weekNum = parseInt(parts[1]);
  
  // Calculate the first day of the year
  const jan1 = new Date(year, 0, 1);
  
  // Find the first Monday of the year (or the Monday of week 1)
  const dayOfWeek = jan1.getDay();
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const firstMonday = new Date(year, 0, 1 + daysToMonday);
  
  // If first Monday is after Jan 4, it's actually week 1 of previous year logic
  // ISO week 1 contains Jan 4th
  const jan4 = new Date(year, 0, 4);
  const jan4DayOfWeek = jan4.getDay();
  const jan4Monday = new Date(year, 0, 4 - (jan4DayOfWeek === 0 ? 6 : jan4DayOfWeek - 1));
  
  // Calculate the start of the requested week
  const weekStart = new Date(jan4Monday);
  weekStart.setDate(jan4Monday.getDate() + (weekNum - 1) * 7);
  
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  
  const formatDay = function(d) {
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const yr = d.getFullYear().toString().slice(-2);
    return day + '/' + month + '/' + yr;
  };
  
  return {
    start: formatDay(weekStart),
    end: formatDay(weekEnd)
  };
};

// Default date range - 1 year from Jan 1, 2026
export const DEFAULT_START_DATE = '2026-01-01';
export const DEFAULT_END_DATE = '2026-12-31';

// Get default date range (1 year)
export const getDefaultDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const startDate = format(startOfYear(now), 'yyyy-MM-dd');
  const endDate = format(addYears(startOfYear(now), 1), 'yyyy-MM-dd');
  return { startDate: startDate, endDate: endDate };
};

// Preset date ranges
export const DATE_RANGE_PRESETS = [
  { label: '3 Mesi', months: 3 },
  { label: '6 Mesi', months: 6 },
  { label: '1 Anno', months: 12 },
  { label: '2 Anni', months: 24 },
];
